<script setup lang="ts">
  import { computed, inject, ref, Ref } from 'vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import { getClubCurrencyDefaults } from '@/domain/club/club-currency-defaults';
  import {
    effectiveReturnPercent,
    exampleSpend,
    pointsForSpend,
    valueForPoints,
  } from '@/domain/club/club-program-math';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { IClubProgramDraft } from './club-program-draft';

  const draft = inject<Ref<IClubProgramDraft>>('clubProgramDraft')!;
  const restaurantStore = useRestaurantStore();

  const symbol = computed(() => restaurantStore.currency?.symbol ?? '$');
  const defaults = getClubCurrencyDefaults(
    restaurantStore.currency?.value ?? null
  );

  const advancedOpen = ref(false);

  function toNumber(value: string): number | null {
    if (value === '') return null;
    const num = Number(value);
    return isNaN(num) ? null : num;
  }

  /** Live example recomputed as the numbers are typed. */
  const example = computed(() => {
    const earnConfig = {
      earnUnitAmount: draft.value.earnUnitAmount ?? 0,
      earnPoints: draft.value.earnPoints ?? 0,
    };
    const redeemConfig = {
      redeemPointsCost: draft.value.redeemPointsCost ?? 0,
      redeemValue: draft.value.redeemValue ?? 0,
    };

    const spend = exampleSpend(earnConfig);
    if (!spend) return null;

    const points = pointsForSpend(earnConfig, spend);
    const redeemPoints = redeemConfig.redeemPointsCost;
    const redeemValue = valueForPoints(redeemConfig, redeemPoints);
    if (!points || !redeemValue) return null;

    // Display only: not part of the draft nor of the saved program.
    const percent = effectiveReturnPercent(earnConfig, redeemConfig);

    return {
      spend: spend.toLocaleString('es-AR'),
      points: points.toLocaleString('es-AR'),
      redeemPoints: redeemPoints.toLocaleString('es-AR'),
      redeemValue: redeemValue.toLocaleString('es-AR'),
      percent:
        percent > 0
          ? percent.toLocaleString('es-AR', { maximumFractionDigits: 1 })
          : null,
    };
  });

  const expiryEnabled = computed({
    get: () => draft.value.pointsExpiryMonths !== null,
    set: (value: boolean) => {
      draft.value.pointsExpiryMonths = value ? 12 : null;
    },
  });
</script>

<template>
  <div class="club-step-points">
    <label class="points-toggle">
      <input
        v-model="draft.pointsEnabled"
        class="points-toggle-checkbox"
        type="checkbox"
      />
      <span class="points-toggle-text">
        <span class="points-toggle-title">
          {{ $t('club.wizard.pointsToggle') }}
        </span>
        <span class="points-toggle-hint">
          {{ $t('club.wizard.pointsToggleHint') }}
        </span>
      </span>
    </label>

    <template v-if="draft.pointsEnabled">
      <div class="points-grid">
        <div class="step-field">
          <label class="step-label">{{ $t('club.wizard.earnUnit') }}</label>
          <PosUiInput
            :value="draft.earnUnitAmount"
            type="number"
            inputmode="decimal"
            :prefix="symbol"
            :placeholder="String(defaults.earnUnitAmount)"
            @input="draft.earnUnitAmount = toNumber($event)"
          />
        </div>
        <div class="step-field">
          <label class="step-label">{{ $t('club.wizard.earnPoints') }}</label>
          <PosUiInput
            :value="draft.earnPoints"
            type="number"
            inputmode="numeric"
            :suffix="$t('club.wizard.pointsUnit')"
            :placeholder="String(defaults.earnPoints)"
            @input="draft.earnPoints = toNumber($event)"
          />
        </div>
      </div>

      <!-- Earning above, redeeming below: two distinct concepts. -->
      <div class="points-divider" />

      <div class="points-grid">
        <div class="step-field">
          <label class="step-label">{{ $t('club.wizard.redeemPoints') }}</label>
          <PosUiInput
            :value="draft.redeemPointsCost"
            type="number"
            inputmode="numeric"
            :suffix="$t('club.wizard.pointsUnit')"
            :placeholder="String(defaults.redeemPointsCost)"
            @input="draft.redeemPointsCost = toNumber($event)"
          />
        </div>
        <div class="step-field">
          <label class="step-label">{{ $t('club.wizard.redeemValue') }}</label>
          <PosUiInput
            :value="draft.redeemValue"
            type="number"
            inputmode="decimal"
            :prefix="symbol"
            :placeholder="String(defaults.redeemValue)"
            @input="draft.redeemValue = toNumber($event)"
          />
        </div>
      </div>

      <div v-if="example" class="points-example">
        <PosUiIcon name="lightbulb-outline" class="points-example-icon" />
        <div class="points-example-body">
          <span class="points-example-text">
            {{
              $t('club.wizard.exampleSentence', {
                symbol,
                spend: example.spend,
                points: example.points,
                redeemPoints: example.redeemPoints,
                redeemValue: example.redeemValue,
              })
            }}
          </span>
          <span v-if="example.percent" class="points-example-return">
            {{ $t('club.wizard.exampleReturn', { percent: example.percent }) }}
          </span>
        </div>
      </div>

      <button
        class="advanced-toggle"
        type="button"
        @click="advancedOpen = !advancedOpen"
      >
        <PosUiIcon :name="advancedOpen ? 'chevron-up' : 'chevron-down'" />
        {{ $t('club.wizard.advancedOptions') }}
      </button>

      <div v-if="advancedOpen" class="advanced-panel">
        <label class="advanced-row">
          <input
            v-model="expiryEnabled"
            class="advanced-checkbox"
            type="checkbox"
          />
          <span class="advanced-row-label">
            {{ $t('club.wizard.expiryToggle') }}
          </span>
          <PosUiInput
            v-if="expiryEnabled"
            :value="draft.pointsExpiryMonths"
            type="number"
            inputmode="numeric"
            size="sm"
            class="advanced-inline-input"
            @input="draft.pointsExpiryMonths = toNumber($event)"
          />
          <span v-if="expiryEnabled" class="advanced-row-suffix">
            {{ $t('club.wizard.expiryMonths') }}
          </span>
        </label>

        <div class="advanced-grid">
          <div class="step-field">
            <label class="step-label">
              {{ $t('club.wizard.welcomePoints') }}
            </label>
            <PosUiInput
              :value="draft.welcomePoints"
              type="number"
              inputmode="numeric"
              :suffix="$t('club.wizard.pointsUnit')"
              placeholder="0"
              @input="draft.welcomePoints = toNumber($event)"
            />
            <span class="step-hint">
              {{ $t('club.wizard.welcomePointsHint') }}
            </span>
          </div>
          <div class="step-field">
            <label class="step-label">
              {{ $t('club.wizard.minRedeemPoints') }}
            </label>
            <PosUiInput
              :value="draft.minRedeemPoints"
              type="number"
              inputmode="numeric"
              :suffix="$t('club.wizard.pointsUnit')"
              placeholder="0"
              @input="draft.minRedeemPoints = toNumber($event)"
            />
          </div>
          <div class="step-field">
            <label class="step-label">
              {{ $t('club.wizard.maxRedeemPercent') }}
            </label>
            <PosUiInput
              :value="draft.maxRedeemPercent"
              type="number"
              inputmode="numeric"
              suffix="%"
              placeholder="100"
              @input="draft.maxRedeemPercent = toNumber($event)"
            />
            <span class="step-hint">
              {{ $t('club.wizard.maxRedeemPercentHint') }}
            </span>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
  .club-step-points {
    @apply tw-flex tw-flex-col tw-gap-4;

    .points-toggle {
      @apply tw-flex tw-cursor-pointer tw-items-start tw-gap-3 tw-rounded-xl tw-border tw-border-neutral-200 tw-p-3;

      .points-toggle-checkbox {
        @apply tw-mt-1 tw-accent-orange-600;
        width: 18px;
        height: 18px;
      }

      .points-toggle-text {
        @apply tw-flex tw-flex-col;

        .points-toggle-title {
          @apply tw-text-sm tw-font-semibold tw-text-neutral-900;
        }

        .points-toggle-hint {
          @apply tw-text-xs tw-text-neutral-500;
        }
      }
    }

    .points-grid {
      @apply tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-2;
    }

    .points-divider {
      @apply tw-border-t tw-border-neutral-200;
    }

    .step-field {
      @apply tw-flex tw-flex-col tw-gap-1.5;

      .step-label {
        @apply tw-text-sm tw-font-medium tw-text-neutral-700;
      }

      .step-hint {
        @apply tw-text-xs tw-text-neutral-400;
      }
    }

    .points-example {
      @apply tw-flex tw-items-start tw-gap-2.5 tw-rounded-xl tw-border tw-border-sky-200 tw-bg-sky-50 tw-p-3;

      .points-example-icon {
        @apply tw-mt-0.5 tw-text-lg tw-text-sky-700;
      }

      .points-example-body {
        @apply tw-flex tw-min-w-0 tw-flex-col tw-gap-1;

        .points-example-text {
          @apply tw-text-sm tw-text-sky-900;
        }

        .points-example-return {
          @apply tw-text-xs tw-font-medium tw-text-sky-800;
        }
      }
    }

    .advanced-toggle {
      @apply tw-flex tw-min-h-11 tw-items-center tw-gap-1 tw-self-start tw-text-sm tw-font-medium tw-text-neutral-500 tw-transition-colors hover:tw-text-neutral-800;
    }

    .advanced-panel {
      @apply tw-flex tw-flex-col tw-gap-3 tw-rounded-xl tw-bg-neutral-50 tw-p-3;

      .advanced-row {
        @apply tw-flex tw-min-h-11 tw-cursor-pointer tw-flex-wrap tw-items-center tw-gap-2;

        .advanced-checkbox {
          @apply tw-accent-orange-600;
          width: 18px;
          height: 18px;
        }

        .advanced-row-label {
          @apply tw-text-sm tw-text-neutral-800;
        }

        .advanced-inline-input {
          @apply tw-w-20;
        }

        .advanced-row-suffix {
          @apply tw-text-sm tw-text-neutral-500;
        }
      }

      .advanced-grid {
        @apply tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-2;
      }
    }
  }
</style>
